import { useCallback, useEffect, useRef, useState } from 'react';

const getSpeechRecognition = () =>
  typeof window !== 'undefined'
    ? window.SpeechRecognition || window.webkitSpeechRecognition
    : undefined;

export const useSpeechToText = ({ lang } = {}) => {
  const [listening, setListening] = useState(false);
  const [interimTranscript, setInterimTranscript] = useState('');
  const [finalTranscript, setFinalTranscript] = useState('');
  const [error, setError] = useState(null);
  const recognitionRef = useRef(null);
  const finalRef = useRef('');

  const supported = Boolean(getSpeechRecognition());

  const stop = useCallback(() => {
    if (recognitionRef.current) recognitionRef.current.stop();
  }, []);

  const start = useCallback(() => {
    const SpeechRecognitionImpl = getSpeechRecognition();
    if (!SpeechRecognitionImpl || recognitionRef.current) return;
    const recognition = new SpeechRecognitionImpl();
    recognition.continuous = true;
    recognition.interimResults = true;
    if (lang) recognition.lang = lang;
    finalRef.current = '';
    setFinalTranscript('');
    setInterimTranscript('');
    setError(null);

    recognition.onresult = (event) => {
      let interim = '';
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const result = event.results[i];
        if (result.isFinal) {
          finalRef.current = `${finalRef.current} ${result[0].transcript}`.trim();
        } else {
          interim += result[0].transcript;
        }
      }
      setFinalTranscript(finalRef.current);
      setInterimTranscript(interim);
    };
    recognition.onerror = (event) => setError(event.error || 'speech-error');
    recognition.onend = () => {
      recognitionRef.current = null;
      setListening(false);
      setInterimTranscript('');
    };

    recognitionRef.current = recognition;
    setListening(true);
    try {
      recognition.start();
    } catch (err) {
      recognitionRef.current = null;
      setListening(false);
      setError('start-failed');
    }
  }, [lang]);

  useEffect(
    () => () => {
      if (recognitionRef.current && recognitionRef.current.abort) {
        recognitionRef.current.abort();
      }
    },
    []
  );

  return { supported, listening, interimTranscript, finalTranscript, start, stop, error };
};

export default useSpeechToText;
